import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import type { Withdrawal } from '@entities/withdrawal';
import {
  collapseMerchantHistory,
  confirmationView,
  fundsLabelKey,
  isWithdrawalCancellable,
  mapStateEventList,
  mapWithdrawalDtoToEntity,
  merchantHintKey,
  merchantStageLabelKey,
  WithdrawalStatusBadge,
  withdrawalExplorerUrl,
} from '@entities/withdrawal';
import { ComplianceThread } from '@features/compliance-thread';
import { swrKeys } from '@shared/api/swr/keys';
import { useApiServices } from '@shared/lib/use-api-services';
import { formatLedgerAmount } from '@shared/lib/format-ledger-amount';
import { formatDateTime } from '@shared/lib/format-date';
import { useCopyToClipboard } from '@shared/lib/use-copy-to-clipboard';
import { NetworkCostSummary } from '@widgets/withdrawal/network-cost-summary';
import {
  Button,
  CopyableHash,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
  StatusBanner,
  TypographyMuted,
  useToast,
} from '@shared/ui';

interface MerchantWithdrawalSheetProps {
  withdrawalId: string | null;
  fallback?: Withdrawal | null;
  onOpenChange: (open: boolean) => void;
  onCancel?: (id: string) => Promise<{ success: boolean; error?: string }>;
  onRefresh?: () => void;
}

export function MerchantWithdrawalSheet({
  withdrawalId,
  fallback = null,
  onOpenChange,
  onCancel,
  onRefresh,
}: MerchantWithdrawalSheetProps) {
  const { t } = useTranslation('cabinet');
  const { toast } = useToast();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const { copy } = useCopyToClipboard();
  const services = useApiServices();
  const open = Boolean(withdrawalId);

  const details = useSWR(
    withdrawalId ? ['withdrawal-card', withdrawalId] : null,
    () => services.withdrawals.getWithdrawal(withdrawalId!),
    { refreshInterval: 10000 },
  );
  const history = useSWR(
    withdrawalId ? ['withdrawal-state-events', withdrawalId] : null,
    () => services.withdrawals.listStateEvents(withdrawalId!),
    { refreshInterval: 10000 },
  );
  const messages = useSWR(
    withdrawalId ? swrKeys.compliance.messages(withdrawalId) : null,
    () => services.compliance.listMessages(withdrawalId!),
  );

  const withdrawal = details.data ? mapWithdrawalDtoToEntity(details.data) : fallback;
  const events = mapStateEventList(history.data);
  const steps = collapseMerchantHistory(events);
  const fundsKey = withdrawal ? fundsLabelKey(withdrawal.fundsReservationState) : null;
  const confirmations = confirmationView(withdrawal?.confirmations);
  const explorer = withdrawal?.txid ? withdrawalExplorerUrl(withdrawal.network, withdrawal.txid) : null;
  const stale = Boolean(details.error || history.error);

  const reply = async (text: string) => {
    if (!withdrawalId) return;
    try {
      await services.compliance.replyMessage(withdrawalId, text);
      await messages.mutate();
      onRefresh?.();
    } catch (error) {
      if (error && typeof error === 'object' && 'status' in error && (error.status === 409 || error.status === 429)) {
        onRefresh?.();
        void messages.mutate();
      }
      throw error;
    }
  };

  return (
    <>
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetBody>
          {withdrawal ? (
            <div className="space-y-4 pr-8">
              <SheetHeader>
                <SheetTitle>#{withdrawal.id}</SheetTitle>
              </SheetHeader>
              {stale ? (
                <StatusBanner>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <span>{t('withdrawals.stale')}</span>
                    <Button type="button" variant="outline" size="sm" className="w-full sm:w-auto" onClick={() => { void details.mutate(); void history.mutate(); }}>
                      {t('common.retry', { ns: 'common' })}
                    </Button>
                  </div>
                </StatusBanner>
              ) : null}
              <WithdrawalStatusBadge status={withdrawal.status} state={withdrawal.state} />
              <TypographyMuted>{t(merchantHintKey(withdrawal.state, withdrawal.reasonCode))}</TypographyMuted>
              {fundsKey ? <p className="text-sm text-foreground">{t(fundsKey)}</p> : null}
              <p className="text-lg font-semibold tabular-nums">
                {formatLedgerAmount(withdrawal.amount, { assetLabel: withdrawal.assetCode })}
              </p>
              {withdrawal.netPayoutAmount && withdrawal.netPayoutAmount !== withdrawal.amount ? (
                <p className="text-sm text-foreground">
                  {t('networkCost.recipientAmount')}:{' '}
                  {formatLedgerAmount(withdrawal.netPayoutAmount, { assetLabel: withdrawal.assetCode })}
                </p>
              ) : null}
              {withdrawal.networkCost ? (
                <NetworkCostSummary
                  assetCode={withdrawal.assetCode}
                  withdrawalAmount={withdrawal.amount}
                  summary={withdrawal.networkCost}
                />
              ) : null}
              {withdrawal.ledgerOperationId ? (
                <p className="text-xs text-muted-foreground">
                  {t('networkCost.ledgerOperation')}: {withdrawal.ledgerOperationId}
                </p>
              ) : null}
              <p className="text-sm text-muted-foreground">{withdrawal.network}</p>
              <p className="break-all font-mono text-xs text-muted-foreground">{withdrawal.address}</p>
              <p className="text-sm text-muted-foreground">{formatDateTime(withdrawal.createdAt)}</p>
              {withdrawal.walletKind ? (
                <p className="text-sm text-foreground">{t(`withdrawals.source.${withdrawal.walletKind}`)}</p>
              ) : null}
              {confirmations.kind === 'count' ? (
                <p className="text-sm text-foreground">
                  {t('withdrawals.confirmations.count', { current: confirmations.current, required: confirmations.required })}
                </p>
              ) : null}
              {confirmations.kind === 'finality' ? (
                <p className="text-sm text-foreground">{t('withdrawals.confirmations.finality')}</p>
              ) : null}
              {withdrawal.completedAt ? (
                <p className="text-sm text-muted-foreground">
                  {t('withdrawals.completedAt', { datetime: formatDateTime(withdrawal.completedAt) })}
                </p>
              ) : null}
              {withdrawal.txid ? (
                <div className="space-y-1">
                  <CopyableHash value={withdrawal.txid} onCopy={() => copy(withdrawal.txid!)} />
                  {explorer ? (
                    <a className="text-sm text-foreground underline-offset-2 hover:underline" href={explorer} target="_blank" rel="noreferrer">
                      {withdrawal.txid}
                    </a>
                  ) : null}
                </div>
              ) : null}
              <section className="space-y-2">
                <p className="text-sm font-medium">{t('withdrawals.history.title')}</p>
                {history.error ? <TypographyMuted>{t('withdrawals.history.error')}</TypographyMuted> : null}
                {!history.error && !steps.length ? <TypographyMuted>{t('withdrawals.history.empty')}</TypographyMuted> : null}
                {!history.error && !withdrawal.state ? (
                  <TypographyMuted>{t('withdrawals.history.incomplete')}</TypographyMuted>
                ) : null}
                <ul className="space-y-2">
                  {steps.map((step) => (
                    <li key={`${step.at}-${step.state}`} className="rounded-lg bg-muted/50 px-3 py-2">
                      <p className="text-sm text-foreground">{t(merchantStageLabelKey(step.state, step.state))}</p>
                      <p className="text-xs text-muted-foreground">{formatDateTime(step.at)}</p>
                    </li>
                  ))}
                </ul>
              </section>
              {(messages.data?.data.length || withdrawal.status === 'info_requested' || withdrawal.state === 'info_requested') ? (
                <section className="space-y-2">
                  <p className="text-sm font-medium">{t('compliance.thread.title')}</p>
                  <ComplianceThread
                    messages={messages.data?.data ?? []}
                    canReply={withdrawal.state === 'info_requested' || withdrawal.status === 'info_requested'}
                    reply={reply}
                  />
                </section>
              ) : null}
              {isWithdrawalCancellable(withdrawal.status, withdrawal.state) && onCancel ? (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full text-destructive hover:text-destructive sm:w-auto"
                  onClick={() => setConfirmCancel(true)}
                >
                  {t('withdrawals.actions.cancel')}
                </Button>
              ) : null}
            </div>
          ) : details.error ? (
            <StatusBanner>
              <div className="flex flex-col gap-2">
                <span>{t('withdrawals.stale')}</span>
                <Button type="button" variant="outline" size="sm" className="w-full sm:w-auto" onClick={() => void details.mutate()}>
                  {t('common.retry', { ns: 'common' })}
                </Button>
              </div>
            </StatusBanner>
          ) : null}
        </SheetBody>
      </SheetContent>
    </Sheet>
      <Dialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('withdrawals.actions.cancelConfirmTitle')}</DialogTitle>
            <DialogDescription>{t('withdrawals.actions.cancelConfirmDescription')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirmCancel(false)}>
              {t('withdrawals.actions.back')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                if (!withdrawal || !onCancel) return;
                setConfirmCancel(false);
                void onCancel(withdrawal.id).then((result) => {
                  void details.mutate();
                  onRefresh?.();
                  if (result.success) {
                    toast(t('withdrawals.actions.cancelSuccess'), 'success');
                    return;
                  }
                  if (result.error) toast(result.error, 'error');
                });
              }}
            >
              {t('withdrawals.actions.cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
